import {
    Card,
    CardHeader,
    makeStyles,
    tokens,
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Link,
} from '@fluentui/react-components';
import {
    ChevronUp24Regular,
    ChevronDown24Regular,
    ArrowRight20Regular,
    ChevronDown16Regular,
    ChevronUp16Regular,
} from '@fluentui/react-icons';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';

import { useGuidanceCard } from '../../../libs/hooks/useGuidanceCard';
import { trackStructEvent } from '../../../libs/snowplow';

const useClasses = makeStyles({
    card: {
        width: '100%',
        maxWidth: '700px',
        margin: 'auto 0',
        padding: '0',
        borderRadius: '8px',
        display: 'block',
    },
    header: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ':hover': {
            backgroundColor: 'var(--app-hover-accordion-background)',
        },
    },
    headerTitle: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        lineHeight: tokens.lineHeightBase400,
    },
    expandButton: {
        // Remove all hover effects
        backgroundColor: 'transparent !important',
        color: tokens.colorNeutralForeground1,
        ':hover': {
            backgroundColor: 'transparent !important',
            color: `${tokens.colorNeutralForeground1} !important`,
        },
        ':hover:active': {
            backgroundColor: 'transparent !important',
            color: `${tokens.colorNeutralForeground1} !important`,
        },
        // Target the icon inside
        '& svg': {
            color: `${tokens.colorNeutralForeground1} !important`,
        },
        ':hover svg': {
            color: `${tokens.colorNeutralForeground1} !important`,
        },
    },
    description: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
        borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
        '& p': {
            margin: '0',
        },
    },
    accordionItem: {
        borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
        ':last-child': {
            borderBottom: 'none',
        },
    },
    accordionHeader: {
        padding: `0 calc(${tokens.spacingHorizontalM} + ${tokens.spacingHorizontalXL})`,
        borderRadius: '0',
        backgroundColor: 'var(--app-normal-accordion-background)',
        '& button': {
            padding: `${tokens.spacingVerticalS} 0`,
            borderRadius: '0',
            textAlign: 'left',
            fontWeight: tokens.fontWeightSemibold,
            backgroundColor: 'transparent !important',
            ':hover': {
                backgroundColor: 'transparent !important',
            },
        },
        // Hover state - light purple background
        ':hover': {
            backgroundColor: 'var(--app-hover-accordion-background) !important',
        },
    },
    // Keyboard focus (Tab) - blue border
    accordionHeaderKeyboardFocus: {
        border: `1px solid ${tokens.colorBrandForegroundLink}`,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    // Mouse click focus (selected) - no special styling
    accordionHeaderMouseFocus: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    accordionPanel: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        padding: `0 calc(${tokens.spacingHorizontalM} + ${tokens.spacingHorizontalXL}) ${tokens.spacingVerticalL} calc(${tokens.spacingHorizontalM} + ${tokens.spacingHorizontalXL})`,
        margin: '0',
        '& p': {
            margin: '0',
            marginBottom: tokens.spacingVerticalS,
        },
        '& p:last-child': {
            marginBottom: 0,
        },
    },
    link: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        width: 'fit-content',
        fontWeight: tokens.fontWeightSemibold,
        textDecorationLine: 'underline',
        color: tokens.colorBrandForegroundLink,
        ':hover': {
            color: tokens.colorBrandForegroundLinkHover,
            textDecorationLine: 'none',
        },
    },
    markdownLink: {
        color: tokens.colorBrandForegroundLink,
        textDecorationLine: 'underline',
        ':hover': {
            color: tokens.colorBrandForegroundLinkHover,
            textDecorationLine: 'none',
        },
    },
    askButton: {
        width: 'fit-content',
        height: 'auto',
        whiteSpace: 'normal',
        textAlign: 'left',
        justifyContent: 'flex-start',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '8px',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground4,
            borderTopColor: tokens.colorNeutralForeground3,
            borderRightColor: tokens.colorNeutralForeground3,
            borderBottomColor: tokens.colorNeutralForeground3,
            borderLeftColor: tokens.colorNeutralForeground3,
        },
    },
});

interface GuidanceCardProps {
    onQuerySubmit: (query: string) => void;
}

export const GuidanceCard: React.FC<GuidanceCardProps> = ({ onQuerySubmit }) => {
    const classes = useClasses();
    const [isExpanded, setIsExpanded] = React.useState(true);
    const [openItems, setOpenItems] = React.useState<number[]>([]);
    const [focusedAccordion, setFocusedAccordion] = React.useState<{
        index: number;
        source: 'keyboard' | 'mouse';
    } | null>(null);
    const isMouseDownRef = React.useRef(false);

    const { showCard, content } = useGuidanceCard();

    // Track mouse interactions
    const handleMouseDown = (index: number) => () => {
        isMouseDownRef.current = true;
        setFocusedAccordion({ index, source: 'mouse' });
    };

    // When element receives focus, check if it was from mouse or keyboard
    const handleFocus = (index: number) => () => {
        if (isMouseDownRef.current) {
            // Mouse click caused the focus
            setFocusedAccordion({ index, source: 'mouse' });
        } else {
            // Keyboard (Tab) caused the focus
            setFocusedAccordion({ index, source: 'keyboard' });
        }
        isMouseDownRef.current = false;
    };

    const handleBlur = () => {
        setFocusedAccordion(null);
        isMouseDownRef.current = false;
    };

    const getAccordionHeaderClass = (index: number) => {
        let className = classes.accordionHeader;
        if (focusedAccordion?.index === index) {
            if (focusedAccordion.source === 'keyboard') {
                className += ` ${classes.accordionHeaderKeyboardFocus}`;
            } else {
                className += ` ${classes.accordionHeaderMouseFocus}`;
            }
        }
        return className;
    };

    if (!showCard || !content) {
        return null;
    }

    return (
        <Card className={classes.card}>
            <CardHeader
                className={classes.header}
                header={<span className={classes.headerTitle}>{content.title}</span>}
                action={
                    <Button
                        appearance="subtle"
                        className={classes.expandButton}
                        icon={isExpanded ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
                        onClick={() => {
                            trackStructEvent('aia-chat-guidance-card', 'accordion-behavior', {
                                label: isExpanded ? 'collapse' : 'expand',
                                feature: { feature_name: 'Guidance card', is_core_feature: false },
                            });
                            setIsExpanded(!isExpanded);
                        }}
                    />
                }
            />
            {isExpanded && (
                <>
                    <div className={classes.description}>
                        <ReactMarkdown
                            components={{
                                a: ({ node: _node, href, children }) => (
                                    <Link
                                        href={href}
                                        className={classes.markdownLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => {
                                            trackStructEvent('aia-chat-guidance-card', 'link-click', {
                                                property: 'guidance-card-link-click',
                                                label: href,
                                                feature: { feature_name: 'Guidance card', is_core_feature: false },
                                            });
                                        }}
                                    >
                                        {children}
                                    </Link>
                                ),
                            }}
                        >
                            {content.description}
                        </ReactMarkdown>
                        {content.link && (
                            <Link
                                href={content.link.url}
                                className={classes.link}
                                target="_top"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    if (content.link) {
                                        trackStructEvent('aia-chat-guidance-card', 'link-click', {
                                            label: content.link.url,
                                            feature: { feature_name: 'Guidance card', is_core_feature: false },
                                        });
                                    }
                                }}
                            >
                                <ArrowRight20Regular />
                                {content.link.text}
                            </Link>
                        )}
                    </div>
                    <Accordion
                        collapsible
                        openItems={openItems}
                        onToggle={(_event, data) => {
                            setOpenItems(data.openItems as number[]);
                        }}
                    >
                        {content.sections.map((section, index) => (
                            <AccordionItem value={index} key={index} className={classes.accordionItem}>
                                <div
                                    onMouseDown={handleMouseDown(index)}
                                    onFocus={handleFocus(index)}
                                    onBlur={handleBlur}
                                >
                                    <AccordionHeader
                                        expandIconPosition="end"
                                        className={getAccordionHeaderClass(index)}
                                        expandIcon={
                                            openItems.includes(index) ? (
                                                <ChevronUp16Regular />
                                            ) : (
                                                <ChevronDown16Regular />
                                            )
                                        }
                                    >
                                        {section.title}
                                    </AccordionHeader>
                                </div>
                                <AccordionPanel className={classes.accordionPanel}>
                                    <div>
                                        <ReactMarkdown
                                            components={{
                                                a: ({ node: _node, href, children }) => (
                                                    <Link
                                                        href={href}
                                                        className={classes.markdownLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={() => {
                                                            trackStructEvent('aia-chat-guidance-card', 'link-click', {
                                                                label: href,
                                                                feature: {
                                                                    feature_name: 'Guidance card',
                                                                    is_core_feature: false,
                                                                },
                                                            });
                                                        }}
                                                    >
                                                        {children}
                                                    </Link>
                                                ),
                                            }}
                                        >
                                            {section.body}
                                        </ReactMarkdown>
                                    </div>
                                    {section.link && (
                                        <Link
                                            href={section.link.url}
                                            className={classes.link}
                                            target="_top"
                                            rel="noopener noreferrer"
                                            onClick={() => {
                                                if (section.link) {
                                                    trackStructEvent('aia-chat-guidance-card', 'link-click', {
                                                        label: section.link.url,
                                                        feature: {
                                                            feature_name: 'Guidance card',
                                                            is_core_feature: false,
                                                        },
                                                    });
                                                }
                                            }}
                                        >
                                            <ArrowRight20Regular />
                                            {section.link.text}
                                        </Link>
                                    )}
                                    {section.questions.map((question, qIndex) => (
                                        <Button
                                            key={qIndex}
                                            className={classes.askButton}
                                            appearance="outline"
                                            onClick={() => {
                                                trackStructEvent('aia-chat-guidance-card', 'ask-button-click', {
                                                    label: question.label,
                                                    feature: { feature_name: 'Guidance card', is_core_feature: false },
                                                });
                                                onQuerySubmit(question.query ?? question.label);
                                            }}
                                        >
                                            {question.label}
                                        </Button>
                                    ))}
                                </AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </>
            )}
        </Card>
    );
};
